import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { Search, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppBadge } from "../ui";

const DIFFICULTIES = ["", "Easy", "Medium", "Hard"];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const AssignmentQuestionPicker = ({ selectedQuestions, setSelectedQuestions }) => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [label, setLabel] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filterMeta, setFilterMeta] = useState({ categories: [], labels: [] });

  // Filter options (categories + labels) for the dropdowns.
  useEffect(() => {
    apiClient
      .get("/questions/filters")
      .then((res) => setFilterMeta({
        categories: res.data?.categories || [],
        labels: res.data?.labels || [],
      }))
      .catch(() => setFilterMeta({ categories: [], labels: [] }));
  }, []);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get("/questions/list", { params: { search, category, label, difficulty, page, limit: pageSize } })
      .then((res) => {
        setQuestions(res.data.questions);
        setTotal(res.data.total || 0);
      })
      .catch(() => { setQuestions([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [search, category, label, difficulty, page, pageSize]);

  const toggleQuestion = (qid) => {
    setSelectedQuestions((prev) =>
      prev.includes(qid) ? prev.filter((id) => id !== qid) : [...prev, qid]
    );
  };

  const totalPages = Math.ceil(total / pageSize);
  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) setPage(p);
  };

  // Any filter change resets to page 1 so results stay consistent.
  const resetPage = () => setPage(1);

  const activeFilterCount =
    (category ? 1 : 0) + (label ? 1 : 0) + (difficulty ? 1 : 0);
  const clearFilters = () => {
    setCategory("");
    setLabel("");
    setDifficulty("");
    resetPage();
  };

  const diffColor = { Hard: "rose", Medium: "amber", Easy: "emerald" };

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <div className="asgn-qp">
      <div className="asgn-qp__toolbar">
        <span className="asgn-qp__count">
          <strong>{selectedQuestions.length}</strong> selected
        </span>
        <div className="asgn-qp__search">
          <Search size={14} className="asgn-qp__search-icon" />
          <input
            className="asgn-qp__search-input"
            placeholder="Search questions…"
            value={search}
            onChange={e => { setSearch(e.target.value); resetPage(); }}
          />
          {search && (
            <button type="button" className="asgn-qp__search-clear" onClick={() => { setSearch(""); resetPage(); }}>
              <X size={14} />
            </button>
          )}
        </div>
        <select
          className="asgn-qp__filter-select"
          value={category}
          onChange={e => { setCategory(e.target.value); resetPage(); }}
        >
          <option value="">All Categories</option>
          {filterMeta.categories.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          className="asgn-qp__filter-select"
          value={label}
          onChange={e => { setLabel(e.target.value); resetPage(); }}
        >
          <option value="">All Labels</option>
          {filterMeta.labels.map(l => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
        <select
          className="asgn-qp__filter-select"
          value={difficulty}
          onChange={e => { setDifficulty(e.target.value); resetPage(); }}
        >
          {DIFFICULTIES.map((d, idx) => (
            <option key={d || idx} value={d}>{d || "All Difficulties"}</option>
          ))}
        </select>
        {activeFilterCount > 0 && (
          <button type="button" className="asgn-qp__filter-clear" onClick={clearFilters}>
            <X size={13} /> Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="asgn-qp__loading"><Spinner animation="border" size="sm" /></div>
      ) : questions.length === 0 ? (
        <div className="asgn-qp__empty">No questions found.</div>
      ) : (
        <div className="asgn-qp__table-wrap">
          <table className="asgn-qp__table">
            <thead>
              <tr>
                <th className="asgn-qp__th asgn-qp__th--check"></th>
                <th className="asgn-qp__th">Title</th>
                <th className="asgn-qp__th">Difficulty</th>
                <th className="asgn-qp__th">Categories</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((q) => {
                const checked = selectedQuestions.includes(q._id);
                return (
                  <tr key={q._id} className={`asgn-qp__row ${checked ? 'asgn-qp__row--selected' : ''}`} onClick={() => toggleQuestion(q._id)}>
                    <td className="asgn-qp__td asgn-qp__td--check">
                      <input type="checkbox" className="asgn-qp__checkbox" checked={checked} onChange={() => {}} />
                    </td>
                    <td className="asgn-qp__td asgn-qp__td--title">{q.title}</td>
                    <td className="asgn-qp__td">
                      <AppBadge variant="secondary" color={diffColor[q.difficulty]}>{q.difficulty}</AppBadge>
                    </td>
                    <td className="asgn-qp__td asgn-qp__td--cats">
                      {q.categories?.map((c) => (
                        <AppBadge variant="outline" size="sm" key={c._id || c.id || c}>{c.name || c}</AppBadge>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="asgn-qp__pagination">
        <div className="asgn-qp__page-summary">
          <span className="asgn-qp__page-info">
            {total === 0 ? "No records" : `${rangeStart}–${rangeEnd} of ${total}`}
          </span>
          <label className="asgn-qp__page-size">
            <span>Rows</span>
            <select
              className="asgn-qp__filter-select"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {PAGE_SIZE_OPTIONS.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
        {totalPages > 1 && (
          <div className="asgn-qp__page-controls">
            <span className="asgn-qp__page-info">Page {page} of {totalPages}</span>
            <div className="asgn-qp__page-btns">
              <button type="button" className="asgn-qp__page-btn" onClick={() => handlePageChange(1)} disabled={page === 1}><ChevronsLeft size={16} /></button>
              <button type="button" className="asgn-qp__page-btn" onClick={() => handlePageChange(page - 1)} disabled={page === 1}><ChevronLeft size={16} /></button>
              <button type="button" className="asgn-qp__page-btn" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}><ChevronRight size={16} /></button>
              <button type="button" className="asgn-qp__page-btn" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}><ChevronsRight size={16} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentQuestionPicker;
